import React from 'react';
import PropTypes from 'prop-types';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Warning from '@material-ui/icons/Warning';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Button,
  Divider,
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import moment from 'logic/moment';
import { isPaidPeriodActive } from 'logic/cottage';
import PATHS from 'constants/paths';
import CottageStats from './Stats';
import LastMinuteDeals from './LastMinuteDialog';
import UpdateCalendarContainer from '../containers/UpdateCalendarDialog';

const styles = makeStyles((theme) => ({
  sup: {
    fontSize: '0.7em',
  },
  paper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    marginBottom: theme.spacing(1),
  },
  textSecondary: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    width: 18,
    height: 18,
    marginRight: theme.spacing(0.5),
  },
  delete: {
    color: theme.palette.grey[600],
  },
  flexWrap: {
    flexWrap: 'wrap',
  },
  group: {
    display: 'flex',
    alignItems: 'center',
  },
  img: {
    marginRight: theme.spacing(2),
    width: 46,
    lineHeight: 0,
    flex: '0 0 46px',
  },
}));

const Cottage = ({
  cottage,
  title,
  defaultExpanded,
  disabledButtons,
  onActivate,
  onHideRemoveOpen,
}) => {
  const classes = styles();
  const paid = isPaidPeriodActive(cottage);

  const imgSrc = cottage.images && cottage.images[0] ? cottage.images[0].url : null;

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <div className={classes.group}>
          {imgSrc && (
            <div className={classes.img}>
              <img src={`${imgSrc}?w=46&h=46&fit=crop`} alt="" />
            </div>
          )}
          <div>
            <Typography>
              #{cottage.id} - {title}
            </Typography>
            <Typography color="textSecondary" className={classes.textSecondary}>
              {cottage.reviewed_at && (
                <>
                  {(cottage.hidden_at || (!paid && !cottage.commission)) && (
                    <Warning color="disabled" classes={{ root: classes.icon }} />
                  )}
                  {cottage.hidden_at && <FormattedMessage id="Hidden" defaultMessage="Hidden" />}
                  {!cottage.hidden_at && !paid && !cottage.commission && (
                    <FormattedMessage id="Unpaid" defaultMessage="Unpaid" />
                  )}
                  {!cottage.hidden_at && (paid || cottage.commission) && (
                    <FormattedMessage id="Active" defaultMessage="Active" />
                  )}
                </>
              )}
              {!cottage.reviewed_at && (
                <>
                  {cottage.asked_for_review_at ? (
                    <FormattedMessage id="Sent for review" defaultMessage="Sent for review" />
                  ) : (
                    <FormattedMessage id="Incomplete" defaultMessage="Incomplete" />
                  )}
                </>
              )}
            </Typography>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Typography>
              <FormattedMessage id="Area" defaultMessage="Area" />: {cottage.area}m
              <sup className={classes.sup}>2</sup>
            </Typography>
            {cottage.beds && (
              <Typography>
                <FormattedMessage id="Beds" defaultMessage="Beds" />: {cottage.beds}
                {!!cottage.extra_beds && [' + ', cottage.extra_beds]}
              </Typography>
            )}
          </Grid>
          {cottage.paid_until && cottage.commission === false && (
            <Grid item xs={12} sm={3}>
              <Typography>
                <FormattedMessage id="Paid until" defaultMessage="Paid until:" />
              </Typography>
              <Typography>{cottage.paid_until}</Typography>
            </Grid>
          )}
          {cottage.gallery_until && moment().isBefore(cottage.gallery_until) && (
            <Grid item xs={12} sm={3}>
              <Typography>
                <FormattedMessage id="Gallery until" defaultMessage="Rank boost until:" />
              </Typography>
              <Typography>{moment(cottage.gallery_until).format('L')}</Typography>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
      <Divider />
      <AccordionActions className={classes.flexWrap}>
        {!cottage.hidden_at && cottage.reviewed_at && (
          <Button size="small" component="a" href={`/sv/stuga/${cottage.id}`} target="_blank">
            <FormattedMessage id="View Ad" defaultMessage="View Ad" />
          </Button>
        )}
        {!cottage.verified_at && cottage.asked_for_review_at && !cottage.reviewed_at && (
          <Button
            size="small"
            color="primary"
            disabled={disabledButtons}
            component={Link}
            to={PATHS.cottages.verify.replace(':cottageId', cottage.id)}
          >
            <FormattedMessage id="cottages.verifyYourAd" defaultMessage="Verify your ad" />
          </Button>
        )}
        {cottage.reviewed_at && <LastMinuteDeals cottageId={cottage.id} />}
        {cottage.reviewed_at && <CottageStats id={cottage.id} />}
        {cottage.commission === true && (
          <Button
            size="small"
            component={Link}
            to={PATHS.hostFiles.cottage.replace(':cottageId', cottage.id)}
          >
            <FormattedMessage id="cottages.files" defaultMessage="Files" />
          </Button>
        )}
        <Button
          size="small"
          color="secondary"
          disabled={disabledButtons}
          onClick={onHideRemoveOpen}
        >
          {cottage.hidden_at || !cottage.reviewed_at ? (
            <FormattedMessage id="Delete" defaultMessage="Delete" />
          ) : (
            <FormattedMessage id="cottages.hideDelete" defaultMessage="Hide / Delete" />
          )}
        </Button>
        {!cottage.hidden_at && !!cottage.reviewed_at && (
          <Button
            size="small"
            component={Link}
            to={PATHS.cottages.payment.replace(':cottageId', cottage.id)}
          >
            {cottage.canRenew ? (
              <FormattedMessage id="Renew Ad" defaultMessage="Renew Ad" />
            ) : (
              <FormattedMessage id="Buy Addons" defaultMessage="Buy Addons" />
            )}
          </Button>
        )}
        {cottage.hidden_at && cottage.reviewed_at && (
          <Button size="small" disabled={disabledButtons} onClick={onActivate}>
            <FormattedMessage id="Activate" defaultMessage="Activate" />
          </Button>
        )}
        {cottage.reviewed_at && (
          <UpdateCalendarContainer cottageId={cottage.id} disabledButtons={disabledButtons} />
        )}
        <Button
          size="small"
          color="primary"
          component={Link}
          disabled={disabledButtons}
          to={PATHS.cottages.edit.replace(':cottageId', cottage.id)}
        >
          {!cottage.asked_for_review_at && (
            <FormattedMessage id="Finalize" defaultMessage="Finalize" />
          )}
          {cottage.asked_for_review_at && <FormattedMessage id="Edit" defaultMessage="Edit" />}
        </Button>
      </AccordionActions>
    </Accordion>
  );
};

Cottage.propTypes = {
  cottage: PropTypes.shape({
    area: PropTypes.number,
    asked_for_review_at: PropTypes.string,
    beds: PropTypes.number,
    canRenew: PropTypes.bool,
    commission: PropTypes.bool,
    extra_beds: PropTypes.number,
    gallery_until: PropTypes.string,
    hidden_at: PropTypes.string,
    id: PropTypes.number.isRequired,
    images: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string })),
    paid_at: PropTypes.string,
    paid_until: PropTypes.string,
    reviewed_at: PropTypes.string,
    title_sv: PropTypes.string,
    verified_at: PropTypes.string,
  }).isRequired,
  title: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  disabledButtons: PropTypes.bool,
  onActivate: PropTypes.func.isRequired,
  onHideRemoveOpen: PropTypes.func.isRequired,
};

export default Cottage;
